/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import StorefrontSignInRoute from "storefronts/StorefrontSignInRoute";
import StorefrontRegisterRoute from "storefronts/StorefrontRegisterRoute";
import StorefrontPage from "storefronts/StorefrontPage";
import ValveBodyDetailPublic from "pages/catalog/ValveBodyDetailPublic";
import CustomerAccount from "pages/customer/CustomerAccount";
import RegisterCustomer from "pages/customer/RegisterCustomer";
import Privacy from "pages/legal/Privacy";
import DeleteAccount from "pages/legal/DeleteAccount";
import CustomerProfile from "pages/customer/CustomerProfile";
import CustomerDeleteAccount from "pages/customer/CustomerDeleteAccount";
import CustomerOrders from "pages/customer/CustomerOrders";
import CustomerOrderDetail from "pages/customer/CustomerOrderDetail";
import CustomerDistributorRequests, {
  CustomerDistributorRequestDetail,
} from "pages/customer/CustomerDistributorRequests";
import CustomerDistributorCustomers from "pages/customer/CustomerDistributorCustomers";
import CustomerAddresses from "pages/customer/CustomerAddresses";
import CustomerValveBodies from "pages/customer/CustomerValveBodies";
import CustomerValveBodyDetail from "pages/customer/CustomerValveBodyDetail";
import CustomerValveBodyCoreReturn from "pages/customer/CustomerValveBodyCoreReturn";
import CustomerCoreReturns from "pages/customer/CustomerCoreReturns";
import CustomerValveBodyWarranty from "pages/customer/CustomerValveBodyWarranty";
import CustomerWarrantyClaimDetail from "pages/customer/CustomerWarrantyClaimDetail";
import CustomerWarrantyClaimChat from "pages/customer/CustomerWarrantyClaimChat";
import RegisterValveBody from "pages/customer/RegisterValveBody";
import PostLogin from "pages/auth/PostLogin";
import MobileQrDeal from "mobile/public/MobileQrDeal";

// LEGACY PUBLIC-WEB DESKTOP ERP: DECOMMISSIONED (2026-10-08).
// The old desktop ERP pages (pages/purchase, pages/ledger, pages/invoices, ...)
// are no longer routed. Their URLs render <LegacyStaffRedirect>, which
// hard-navigates to the matching staff-erp URL under /staff. The page source files
// are kept but unreachable. Map: lib/legacyErpRedirects.js. Docs:
// docs/public-web-legacy-erp-decommission.md. Do not re-add staff pages here.
import LegacyStaffRedirect from "components/LegacyStaffRedirect";
import { LEGACY_ERP_REDIRECTS } from "lib/legacyErpRedirects";
import Restricted from "pages/restricted/Restricted";

const routes = [
  {
    type: "route",
    name: "Home",
    key: "home",
    route: "/",
    component: <StorefrontPage page="home" />,
    private: false,
  },
  {
    type: "route",
    name: "Catalog",
    key: "catalog",
    route: "/catalog",
    component: <StorefrontPage page="catalog" />,
    private: false,
  },
  {
    type: "route",
    name: "Register",
    key: "register",
    route: "/register",
    component: <StorefrontRegisterRoute />,
    private: false,
  },
  {
    type: "route",
    name: "QR Mobile Flow",
    key: "qr-mobile-flow",
    route: "/qr",
    component: <MobileQrDeal />,
    private: false,
  },
  {
    type: "route",
    name: "Privacy Policy",
    key: "privacy",
    route: "/privacy",
    component: <Privacy />,
    private: false,
  },
  {
    type: "route",
    name: "Delete Account",
    key: "delete-account",
    route: "/delete-account",
    component: <DeleteAccount />,
    private: false,
  },
  {
    type: "route",
    name: "Distributor Application",
    key: "distributor-application",
    route: "/distributor/apply",
    component: <RegisterCustomer />,
    private: false,
  },
  {
    type: "route",
    name: "Partner Application",
    key: "partner-application",
    route: "/partners/apply",
    component: <RegisterCustomer />,
    private: false,
  },
  {
    type: "route",
    name: "Cart",
    key: "cart",
    route: "/cart",
    component: <StorefrontPage page="cart" />,
    private: false,
  },
  {
    type: "route",
    name: "Checkout",
    key: "checkout",
    route: "/checkout",
    component: <StorefrontPage page="checkout" />,
    private: true,
    // Portal permissions will be enforced later.
  },
  {
    type: "route",
    name: "Product Detail",
    key: "product-detail",
    route: "/catalog/:documentId",
    component: <StorefrontPage page="productDetail" />,
    private: false,
  },
  {
    type: "route",
    name: "Account",
    key: "account",
    route: "/account",
    component: <CustomerAccount />,
    private: true,
    // Intentionally no permissionKey yet; portal permissions will be enforced later.
  },
  {
    type: "route",
    name: "Account Profile",
    key: "account-profile",
    route: "/account/profile",
    component: <CustomerProfile />,
    private: true,
  },
  {
    type: "route",
    name: "Delete Account",
    key: "account-delete",
    route: "/account/delete-account",
    component: <CustomerDeleteAccount />,
    private: true,
  },
  {
    type: "route",
    name: "Account Orders",
    key: "account-orders",
    route: "/account/orders",
    component: <CustomerOrders />,
    private: true,
  },
  {
    type: "route",
    name: "Account Order Detail",
    key: "account-order-detail",
    route: "/account/orders/:documentId",
    component: <CustomerOrderDetail />,
    private: true,
  },
  {
    type: "route",
    name: "Distributor Order Requests",
    key: "account-distributor-requests",
    route: "/account/distributor-requests",
    component: <CustomerDistributorRequests />,
    private: true,
  },
  {
    type: "route",
    name: "Distributor Order Request Detail",
    key: "account-distributor-request-detail",
    route: "/account/distributor-requests/:documentId",
    component: <CustomerDistributorRequestDetail />,
    private: true,
  },
  {
    type: "route",
    name: "Distributor Linked Customers",
    key: "account-distributor-customers",
    route: "/account/distributor-customers",
    component: <CustomerDistributorCustomers />,
    private: true,
  },
  {
    type: "route",
    name: "Account Addresses",
    key: "account-addresses",
    route: "/account/addresses",
    component: <CustomerAddresses />,
    private: true,
  },
  {
    type: "route",
    name: "Account Valve Bodies",
    key: "account-valve-bodies",
    route: "/account/valve-bodies",
    component: <CustomerValveBodies />,
    private: true,
  },
  {
    type: "route",
    name: "Account Core Returns",
    key: "account-core-returns",
    route: "/account/core-returns",
    component: <CustomerCoreReturns />,
    private: true,
  },
  {
    type: "route",
    name: "Account Valve Body Detail",
    key: "account-valve-body-detail",
    route: "/account/valve-bodies/:documentId",
    component: <CustomerValveBodyDetail />,
    private: true,
  },
  {
    type: "route",
    name: "Account Valve Body Core Return",
    key: "account-valve-body-core-return",
    route: "/account/valve-bodies/:documentId/core-return",
    component: <CustomerValveBodyCoreReturn />,
    private: true,
  },
  {
    type: "route",
    name: "Account Valve Body Warranty",
    key: "account-valve-body-warranty",
    route: "/account/valve-bodies/:documentId/warranty",
    component: <CustomerValveBodyWarranty />,
    private: true,
  },
  {
    type: "route",
    name: "Account Warranty Claim Detail",
    key: "account-warranty-claim-detail",
    route: "/account/warranty-claims/:documentId",
    component: <CustomerWarrantyClaimDetail />,
    private: true,
  },
  {
    type: "route",
    name: "Account Warranty Claim Chat",
    key: "account-warranty-claim-chat",
    route: "/account/warranty-claims/:documentId/chat",
    component: <CustomerWarrantyClaimChat />,
    private: true,
  },
  {
    type: "route",
    name: "Post Login",
    key: "post-login",
    route: "/post-login",
    component: <PostLogin />,
    private: true,
  },
  {
    type: "route",
    name: "Register Valve Body",
    key: "register-valve-body",
    route: "/register/valve-body/:tag",
    component: <RegisterValveBody />,
    private: false,
  },
  {
    type: "route",
    name: "Valve Body Detail Public",
    key: "valve-body-detail-public",
    route: "/valve-bodies/:documentId",
    component: <ValveBodyDetailPublic />,
    private: false,
  },
  {
    type: "route",
    name: "Restricted",
    key: "restricted",
    route: "/restricted",
    component: <Restricted />,
    private: true,
  },
  {
    type: "route",
    name: "Sign In",
    key: "sign-in",
    route: "/authentication/sign-in",
    component: <StorefrontSignInRoute />,
  },
  // Retired legacy desktop ERP URLs -> staff-erp (/staff). type "route" keeps them
  // out of any desktop nav; private:false so the redirect runs before any
  // public-web auth gate (staff-erp handles its own sign-in).
  ...LEGACY_ERP_REDIRECTS.map(({ key, from, to }) => ({
    type: "route",
    name: `Legacy ERP redirect: ${key}`,
    key: `legacy-erp-${key}`,
    route: from,
    component: <LegacyStaffRedirect to={to} />,
    private: false,
  })),
];

export default routes;
